import type { ReactNode } from 'react';
import { Tooltip } from '@odo/shared-ui';
import { PracticeDoctorLabel } from './PracticeDoctorLabel';
import type { PracticeDoctorLabelFormat } from './practiceDoctorLabelFormat';

export interface PatientPracticeCellProps {
    patientName: string;
    onPatientClick?: () => void;
    /**
     * Exact classes the caller already used for the name span - kept
     * required rather than defaulted, since sites style the name differently
     * (table cell vs. compact list row) and this component only consolidates
     * the risk/subline logic, not per-site typography.
     */
    nameClassName: string;
    /** Wraps the name in a Tooltip with this content when set (Patients.tsx's
     *  full-name-on-truncate tooltip) - omitted entirely otherwise. */
    nameTooltipContent?: string;
    avatar?: ReactNode;
    /** Rendered inline after the name, already wrapped by the caller if it
     *  needs its own flex container (e.g. a priority pill + tag group). */
    badges?: ReactNode;
    practiceName?: string | null;
    practiceId?: string | null;
    doctorName?: string | null;
    /** @default 'doctor-practice' */
    format?: PracticeDoctorLabelFormat;
    /** Text color for the subline at normal risk - see PracticeDoctorLabel's
     *  own `toneClassName` doc for why this stays separate from layout. */
    sublineToneClassName?: string;
    sublineTooltipSide?: 'top' | 'right' | 'bottom' | 'left';
}

const SUBLINE_LAYOUT_CLASS = 'block truncate text-[10px] max-w-full mt-1';

/**
 * The shared "avatar + patient name (+ badges) + practice/doctor subline"
 * table-row layout, used by the Patients page and the Work Queue's patient
 * cell (identical wrapper structure in both before this consolidation).
 * Anything more bespoke (TaskSurface's header line, DetailPanel's
 * copy-account button, SearchDropdown's rows) renders `PracticeDoctorLabel`
 * directly instead - see that component's own doc comment.
 */
export function PatientPracticeCell({
    patientName,
    onPatientClick,
    nameClassName,
    nameTooltipContent,
    avatar,
    badges,
    practiceName,
    practiceId,
    doctorName,
    format = 'doctor-practice',
    sublineToneClassName,
    sublineTooltipSide = 'top'
}: PatientPracticeCellProps) {
    const nameEl = (
        <span onClick={onPatientClick} className={nameClassName}>
            {patientName}
        </span>
    );

    return (
        <div className="flex items-center gap-3 min-w-0">
            {avatar && <div className="w-8 h-8 flex-shrink-0">{avatar}</div>}
            <div className="flex flex-col min-w-0 leading-tight">
                <div className="flex items-center gap-1.5 min-w-0">
                    {nameTooltipContent ? (
                        <Tooltip content={nameTooltipContent}>{nameEl}</Tooltip>
                    ) : (
                        nameEl
                    )}
                    {badges}
                </div>
                <PracticeDoctorLabel
                    practiceName={practiceName}
                    practiceId={practiceId}
                    doctorName={doctorName}
                    format={format}
                    className={SUBLINE_LAYOUT_CLASS}
                    toneClassName={sublineToneClassName}
                    tooltipSide={sublineTooltipSide}
                />
            </div>
        </div>
    );
}
